/**
 * Promo Code Analytics Dashboard (CREG-11)
 *
 * Shows usage statistics, revenue impact, and conversion rates for promo codes.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  Users,
  Tag,
  Loader2,
  Calendar,
  ArrowLeft,
  Percent,
  Gift,
  Sparkles,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import adminService, { type PromoCodeAnalytics } from '../../../../services/adminService';

// ============================================================================
// KPI CARD COMPONENT
// ============================================================================

function KPICard({
  icon: Icon,
  label,
  value,
  subtext,
  trend,
  color = 'purple',
}: {
  icon: React.ElementType;
  label: string;
  value: string | number;
  subtext?: string;
  trend?: { value: number; positive: boolean };
  color?: 'purple' | 'green' | 'blue' | 'amber';
}) {
  const colorClasses = {
    purple: 'bg-[var(--pv-color-surface-sunken)] text-[#004D40] dark:bg-[#004D40]/30 dark:text-[#1B7566]',
    green: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
    blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    amber: 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 p-6">
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        {trend && (
          <span
            className={`text-sm font-medium ${trend.positive ? 'text-green-600' : 'text-red-600'}`}
          >
            {trend.positive ? '+' : ''}
            {trend.value}%
          </span>
        )}
      </div>
      <div className="mt-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
        {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
      </div>
    </div>
  );
}

// ============================================================================
// DISCOUNT TYPE ICON
// ============================================================================

function getDiscountTypeIcon(type: string): React.ElementType {
  switch (type) {
    case 'PERCENTAGE':
      return Percent;
    case 'FIXED_AMOUNT':
      return DollarSign;
    case 'FREE_TRIAL':
      return Gift;
    case 'FEATURE_UNLOCK':
      return Sparkles;
    default:
      return Tag;
  }
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function PromoCodeAnalyticsPage() {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const [analytics, setAnalytics] = useState<PromoCodeAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await adminService.getPromoCodeAnalytics();
      setAnalytics(data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 p-8 flex justify-center items-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#004D40]" />
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="min-h-screen bg-white dark:bg-gray-900 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <Link
              to="/admin/subscriptions/promo-codes"
              className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <h1 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white">
              Promo Code Analytics
            </h1>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border p-8 text-center">
            <p className="text-red-500">{error || 'No analytics data available'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 p-6" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Link
            to="/admin/subscriptions/promo-codes"
            className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-[var(--pv-color-primary)] dark:text-white">
              Promo Code Analytics
            </h1>
            <p className="text-gray-500">Track usage, revenue impact, and conversion rates</p>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <KPICard
            icon={Tag}
            label="Total Usage"
            value={analytics.totalUsage.toLocaleString()}
            subtext="All-time promo code uses"
            color="purple"
          />
          <KPICard
            icon={DollarSign}
            label="Total Revenue"
            value={`${analytics.totalRevenue.toLocaleString()} SAR`}
            subtext="From promo code users"
            color="green"
          />
          <KPICard
            icon={TrendingUp}
            label="Total Discounts"
            value={`${analytics.totalDiscount.toLocaleString()} SAR`}
            subtext="Discount amount given"
            color="amber"
          />
          <KPICard
            icon={Users}
            label="Conversion Rate"
            value={`${(analytics.conversionRate * 100).toFixed(1)}%`}
            subtext="Promo users who subscribed"
            color="blue"
          />
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top Promo Codes */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-[var(--pv-color-primary)] dark:text-white">
              <BarChart3 className="w-5 h-5 text-[#004D40]" />
              Top Promo Codes
            </h2>
            <div className="space-y-4">
              {analytics.topCodes.length > 0 ? (
                analytics.topCodes.map((code, index) => (
                  <div key={code.code} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-[var(--pv-color-surface-sunken)] dark:bg-[#004D40]/30 rounded-full flex items-center justify-center text-sm font-medium text-[#004D40]">
                        {index + 1}
                      </span>
                      <code className="font-mono text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                        {code.code}
                      </code>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {code.usageCount} uses
                      </p>
                      <p className="text-xs text-gray-500">
                        {code.revenue.toLocaleString()} SAR revenue
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No promo code usage yet</p>
              )}
            </div>
          </div>

          {/* Discount Type Breakdown */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-[var(--pv-color-primary)] dark:text-white">
              <Tag className="w-5 h-5 text-[#004D40]" />
              By Discount Type
            </h2>
            <div className="space-y-4">
              {analytics.byDiscountType && analytics.byDiscountType.length > 0 ? (
                analytics.byDiscountType.map((item) => {
                  const Icon = getDiscountTypeIcon(item.type);
                  return (
                    <div key={item.type} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                          <Icon className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </div>
                        <span className="capitalize text-sm font-medium text-gray-700 dark:text-gray-300">
                          {item.type.replace(/_/g, ' ').toLowerCase()}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {item.count} codes
                        </p>
                        <p className="text-xs text-gray-500">
                          {item.totalDiscount.toLocaleString()} SAR total discount
                        </p>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-500 text-center py-4">No data available</p>
              )}
            </div>
          </div>
        </div>

        {/* Recent Usage Timeline */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2 text-[var(--pv-color-primary)] dark:text-white">
            <Calendar className="w-5 h-5 text-[#004D40]" />
            Recent Usage (Last 30 Days)
          </h2>
          {analytics.recentUsage && analytics.recentUsage.length > 0 ? (
            <div className="h-48 flex items-end gap-1">
              {analytics.recentUsage.map((day) => {
                const maxCount = Math.max(...analytics.recentUsage.map((d) => d.count));
                const heightPercent = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                return (
                  <div
                    key={day.date}
                    className="flex-1 bg-[#DDEEEB] dark:bg-[#004D40] rounded-t hover:bg-[#B7DDD7] dark:hover:bg-[#003930] transition-colors cursor-pointer group relative"
                    style={{
                      height: `${Math.max(10, heightPercent)}%`,
                    }}
                    title={`${day.date}: ${day.count} uses, ${day.discount} SAR discount`}
                  >
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                      {day.date}
                      <br />
                      {day.count} uses
                      <br />
                      {day.discount.toLocaleString()} SAR
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No recent usage data</p>
          )}
          {analytics.recentUsage && analytics.recentUsage.length > 0 && (
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span>{analytics.recentUsage[0]?.date}</span>
              <span>{analytics.recentUsage[analytics.recentUsage.length - 1]?.date}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
